import React, { FunctionComponent, useEffect, useState, useMemo }  from 'react';
import { RouteComponentProps, useHistory } from 'react-router-dom';
import useSWRMutation from 'swr/mutation';

import {
    FooterTeaser,
    Header,
    Section,
    SiteLogo,
    SiteName,
    SubHeader,
    TradingBlockLink,
    MarketDataQuestionnaireForm,
    MarketDataQuestionnaireDescription,
    MARKET_DATA_QUESTIONNAIRE_HEADER,
} from '@tradingblock/components';
import { config as Config } from '../clientConfig';
import { Groups, ResponseCode, TradingBlockEnvironments, UserProfileLevel } from '@tradingblock/types';
import { ApiFactory, GetDocumentStatusUrl, GetUrlParams } from '@tradingblock/api';
import { getEnvironment, toHashParams } from '../utils';

export const MarketDataQuestionnaire: FunctionComponent<RouteComponentProps> = ({ location }) => {
    const siteGrp = Config.siteGrp;
    const group = siteGrp === Groups.mb ? Groups.mb: Groups.tb;
    const urlParams = GetUrlParams(location.search);
    const token: string = urlParams['token'];
    const accessToken: string = urlParams['accessToken'];
    const accountId: number = Number.parseInt(urlParams['accountId']);
    const redirect: string = urlParams['redirect'];
    const next: string = urlParams['next'];
    const userLevel: UserProfileLevel = urlParams['userLevel'];
    const pending: boolean = urlParams['pending'] === 'true';
    const env = getEnvironment(location);
    const isVirtual = env === TradingBlockEnvironments.virtual;

    const [pageLoadedWithJs, setPageLoadedWithJs] = useState<boolean>(false);
    const [triggerGetDocumentStatusRetryCount, setTriggerGetDocumentStatusRetryCount] = useState<number>(0);

    const history = useHistory();

    const api = useMemo(() => ApiFactory(Config.dashboard.tradingApi, accessToken), [accessToken]);

    const {
        data: getDocumentStatusData,
        trigger: triggerGetDocumentStatus,
        error: getDocumentStatusError
    } = useSWRMutation(
        `${Config.dashboard.tradingApi}/${GetDocumentStatusUrl}`,
        (_url: string, { arg }: { arg: { accountId: number } }) => api.accounts.getDocumentStatus(arg.accountId),
    );

    useEffect(() => {
        setPageLoadedWithJs(true);
    }, []);

    // If the account ID is set in state and the document status data is not set or the get document status endpoint returned an error
    // call the get document status endpoint.
    useEffect(() => {
        if (
            accountId &&
            userLevel === UserProfileLevel.Account &&
            !pending &&
            !isVirtual &&
            triggerGetDocumentStatusRetryCount <= 5 &&
            (!getDocumentStatusData || getDocumentStatusData.responseCode !== 0 || getDocumentStatusError)) {
            // If the request to get the user's document status data responds with a code of 200 we need to redirect the user to log in again.
            if (getDocumentStatusData && getDocumentStatusData.responseCode === 200) {
                history.push('/');
                return;
            }

            triggerGetDocumentStatus({ accountId });
            setTriggerGetDocumentStatusRetryCount(triggerGetDocumentStatusRetryCount + 1);
        }
    }, [accountId, getDocumentStatusData, getDocumentStatusError, userLevel, pending, isVirtual]);

    const onSubmissionFailure = (responseCode?: ResponseCode) => {
        // If the access token is expired (10hrs if not used and 24hrs if used) we need to route the user to the login page to get a new access token.
        if (responseCode === 200) {
            history.push('/');
        }
    }

    const onSubmissionSuccess = () => {
        if (
            userLevel === UserProfileLevel.Account &&
            !pending &&
            !isVirtual &&
            getDocumentStatusData &&
            getDocumentStatusData.payload &&
            getDocumentStatusData.responseCode === 0 &&
            typeof getDocumentStatusData.payload === 'object'
        ) {
            if (Object.values(getDocumentStatusData.payload).includes(false)) {
                history.push(`/updated-agreements?token=${token}&accessToken=${accessToken}&accountId=${accountId}`);
                return;
            }
        }

        const nextParam = next ? `?next=${next}` : '';
        const redirectUrl = `${redirect || ''}${nextParam}${toHashParams({ id_token: token, grp: group })}`

        window.location.href = redirectUrl;
    }

    return (
        <>
            <Header>
                <SubHeader>
                    <SiteLogo siteGroup={group} />
                    <nav className="nav-utilities">
                        <ul>
                            <li>
                                <TradingBlockLink siteGroup={group} origin={group} to="LegacySiteHomeUrl">
                                    &uarr; Go to <SiteName siteGroup={group} /> Legacy
                                </TradingBlockLink>
                            </li>
                            <li>
                                <TradingBlockLink siteGroup={group} origin={group} to="CustomerServiceUrl">
                                    Contact us
                                </TradingBlockLink>
                            </li>
                        </ul>
                    </nav>
                </SubHeader>
            </Header>
            <main className="market-data-questionnaire">
                <Section>
                    <div className="content">
                        <h1>{MARKET_DATA_QUESTIONNAIRE_HEADER}</h1>

                        <MarketDataQuestionnaireDescription />

                        {pageLoadedWithJs &&
                            <MarketDataQuestionnaireForm
                                api={api}
                                rootRestHubUrl={Config.dashboard.tradingApi}
                                onSubmissionFailure={onSubmissionFailure}
                                onSubmissionSuccess={onSubmissionSuccess}
                                accountId={accountId}
                            />
                        }

                        <noscript>
                            <div className="alert alert-danger">
                                <h4 className="error" style={{ marginBottom: 0 }}>
                                    JavaScript is required for this site.
                                </h4>
                            </div>
                        </noscript>
                    </div>
                </Section>
            </main>
            <footer className="footer">
                <div className="content">
                    <FooterTeaser siteGroup={group} />
                </div>
            </footer>
        </>
    );
};
